import { useMemo, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import { BrandMark } from '@/components/BrandMark';
import { Input } from '@/components/ui';
import { useBrandReveal, useReference } from '@/hooks/useApp';
import { cn } from '@/lib/cn';
import type { Make, Model } from '@/lib/types';

/**
 * Make and model, chosen in that order and never mixed.
 *
 * The previous version poured every model of every selected make into one flat list, which meant an
 * "A7" and a "7" sat next to each other with nothing to say which was the Audi and which the BMW.
 * Worse, "Serija 3", "3" and "300" all read as plausible neighbours. Scoping models under the make
 * that owns them removes the ambiguity structurally rather than by relabelling anything.
 */

function normalise(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/š/g, 's')
    .replace(/đ/g, 'd')
    .replace(/č|ć/g, 'c')
    .replace(/ž/g, 'z');
}

function useModelsByMake() {
  const { data: reference } = useReference();

  return useMemo(() => {
    const grouped = new Map<number, Model[]>();

    for (const model of reference?.models ?? []) {
      const existing = grouped.get(model.makeId);
      if (existing) {
        existing.push(model);
      } else {
        grouped.set(model.makeId, [model]);
      }
    }

    for (const models of grouped.values()) {
      models.sort((a, b) => a.name.localeCompare(b.name, 'sr'));
    }

    return grouped;
  }, [reference]);
}

/* ------------------------------------------------------------------------------------------- */
/* Step one: the marque.                                                                        */
/* ------------------------------------------------------------------------------------------- */

interface MakeGridProps {
  selectedIds: number[];
  onPick: (make: Make) => void;
  /** Rendered above the grid; the two callers word it differently. */
  emptyHint?: string;
}

export function MakeGrid({ selectedIds, onPick, emptyHint }: MakeGridProps) {
  const { data: reference } = useReference();
  const [term, setTerm] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Memoised rather than `reference?.makes ?? []` inline: a fresh empty array on every render would
  // invalidate the filter below each time, and the lint rule that catches it is an error here.
  const makes = useMemo(() => reference?.makes ?? [], [reference]);
  const query = normalise(term.trim());

  const matches = useMemo(() => {
    if (!query) {
      // Twenty popular marques cover the overwhelming majority of the Serbian market; the long tail
      // is one tap away rather than forty rows of scrolling in front of everybody.
      return showAll ? makes : makes.filter((make) => make.isPopular);
    }

    return makes.filter((make) => normalise(make.name).includes(query));
  }, [makes, query, showAll]);

  const hiddenCount = makes.length - makes.filter((make) => make.isPopular).length;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          size={15}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-faint"
        />
        <Input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Pronađi marku…"
          aria-label="Pretraga marki"
          className="h-10 pl-9 text-sm"
        />
      </div>

      {emptyHint && !query && <p className="text-xs text-faint">{emptyHint}</p>}

      <div className="grid grid-cols-2 gap-2">
        {matches.map((make, index) => {
          const selected = selectedIds.includes(make.id);

          return (
            <button
              key={make.id}
              type="button"
              onClick={() => onPick(make)}
              aria-pressed={selected}
              style={{ '--i': Math.min(index, 14) } as React.CSSProperties}
              className={cn(
                'group animate-fade-in-up stagger press relative flex items-center gap-2.5 overflow-hidden rounded-xl border p-2.5 text-left',
                'transition duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
                selected
                  ? 'border-accent bg-accent-soft'
                  : 'border-default bg-raised hover:-translate-y-0.5 hover:border-strong hover:shadow-card',
              )}
            >
              <BrandMark make={make} size="sm" />
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-[13px] font-medium',
                  selected ? 'text-accent' : 'text-body',
                )}
              >
                {make.name}
              </span>
              {selected && <Check size={14} className="shrink-0 text-accent" />}
            </button>
          );
        })}
      </div>

      {matches.length === 0 && (
        <p className="py-4 text-center text-sm text-faint">Nema marke sa tim imenom.</p>
      )}

      {!query && !showAll && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-default py-2.5 text-xs font-medium text-muted transition hover:border-strong hover:text-accent"
        >
          Prikaži sve marke
          <span className="text-faint">({hiddenCount})</span>
          <ChevronDown size={13} />
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------- */
/* Step two: the models, always inside the make that owns them.                                 */
/* ------------------------------------------------------------------------------------------- */

function ModelChips({
  models,
  selectedIds,
  onToggle,
}: {
  models: Model[];
  selectedIds: number[];
  onToggle: (model: Model) => void;
}) {
  const [term, setTerm] = useState('');
  const query = normalise(term.trim());

  const visible = query ? models.filter((model) => normalise(model.name).includes(query)) : models;

  return (
    <div className="space-y-2.5">
      {/* A marque like Mercedes carries sixty models. Below that a search box is just clutter. */}
      {models.length > 14 && (
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint"
          />
          <Input
            type="search"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Pronađi model…"
            aria-label="Pretraga modela"
            className="h-9 pl-8 text-[13px]"
          />
        </div>
      )}

      <div className="flex max-h-64 flex-wrap gap-1.5 overflow-y-auto overscroll-contain pr-0.5">
        {visible.map((model, index) => {
          const selected = selectedIds.includes(model.id);

          return (
            <button
              key={model.id}
              type="button"
              onClick={() => onToggle(model)}
              aria-pressed={selected}
              style={{ '--i': Math.min(index, 12) } as React.CSSProperties}
              className={cn(
                'animate-fade-in stagger press rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition',
                selected
                  ? 'border-transparent bg-accent text-on-accent shadow-sm'
                  : 'border-default bg-raised text-muted hover:border-strong hover:text-body',
              )}
            >
              {model.name}
            </button>
          );
        })}
      </div>

      {visible.length === 0 && (
        <p className="py-2 text-center text-xs text-faint">Nema modela sa tim imenom.</p>
      )}
    </div>
  );
}

interface Props {
  makeIds: number[];
  modelIds: number[];
  onChange: (next: { makeIds: number[]; modelIds: number[] }) => void;
}

export function MakeModelFilter({ makeIds, modelIds, onChange }: Props) {
  const { data: reference } = useReference();
  const modelsByMake = useModelsByMake();
  const { reveal } = useBrandReveal();

  // Which make's models are on screen. Opening one collapses the rest, because two long model
  // lists open at once in a 320px column is exactly the wall of text this replaced.
  const [openMakeId, setOpenMakeId] = useState<number | null>(null);
  const [picking, setPicking] = useState(false);

  const selectedMakes = useMemo(
    () =>
      makeIds
        .map((id) => reference?.makes.find((make) => make.id === id))
        .filter((make): make is Make => Boolean(make)),
    [makeIds, reference],
  );

  const addMake = (make: Make) => {
    if (makeIds.includes(make.id)) {
      // Tapping an already-chosen marque in the picker means "show me its models", not "drop it".
      setOpenMakeId(make.id);
      setPicking(false);
      return;
    }

    reveal(make);
    onChange({ makeIds: [...makeIds, make.id], modelIds });
    setOpenMakeId(make.id);
    setPicking(false);
  };

  const removeMake = (make: Make) => {
    const owned = new Set((modelsByMake.get(make.id) ?? []).map((model) => model.id));

    // Dropping a marque has to drop its models too, or the URL keeps filtering on an A7 whose Audi
    // is long gone and the results silently stay empty.
    onChange({
      makeIds: makeIds.filter((id) => id !== make.id),
      modelIds: modelIds.filter((id) => !owned.has(id)),
    });

    if (openMakeId === make.id) {
      setOpenMakeId(null);
    }
  };

  const toggleModel = (model: Model) => {
    onChange({
      makeIds,
      modelIds: modelIds.includes(model.id)
        ? modelIds.filter((id) => id !== model.id)
        : [...modelIds, model.id],
    });
  };

  if (selectedMakes.length === 0 || picking) {
    return (
      <div className="space-y-3">
        {picking && selectedMakes.length > 0 && (
          <button
            type="button"
            onClick={() => setPicking(false)}
            className="flex items-center gap-1.5 text-xs font-medium text-muted transition hover:text-accent"
          >
            <ArrowLeft size={13} />
            Nazad na izabrane marke
          </button>
        )}

        <MakeGrid
          selectedIds={makeIds}
          onPick={addMake}
          emptyHint={
            selectedMakes.length === 0 ? 'Prvo izaberite marku, pa onda modele te marke.' : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {selectedMakes.map((make) => {
        const models = modelsByMake.get(make.id) ?? [];
        const chosen = modelIds.filter((id) => models.some((model) => model.id === id));
        const open = openMakeId === make.id;

        return (
          <div
            key={make.id}
            className={cn(
              'animate-fade-in-up overflow-hidden rounded-xl border transition duration-300',
              open ? 'border-strong bg-raised shadow-card' : 'border-default bg-raised',
            )}
          >
            <div className="flex items-center gap-2.5 p-2.5">
              <button
                type="button"
                onClick={() => setOpenMakeId(open ? null : make.id)}
                aria-expanded={open}
                className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
              >
                <BrandMark make={make} size="md" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-body">{make.name}</span>
                  <span className="block truncate text-[11px] text-faint">
                    {chosen.length > 0
                      ? `${chosen.length} ${chosen.length === 1 ? 'model' : 'modela'}`
                      : `Svi modeli (${models.length})`}
                  </span>
                </span>
                <ChevronDown
                  size={16}
                  className={cn('shrink-0 text-faint transition duration-300', open && 'rotate-180')}
                />
              </button>

              <button
                type="button"
                onClick={() => removeMake(make)}
                aria-label={`Ukloni ${make.name}`}
                className="press flex size-8 shrink-0 items-center justify-center rounded-lg text-faint transition hover:bg-sunken hover:text-danger"
              >
                <X size={15} />
              </button>
            </div>

            {open && (
              <div className="animate-fade-in border-t border-default px-2.5 pt-2.5 pb-3">
                <ModelChips models={models} selectedIds={modelIds} onToggle={toggleModel} />
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => setPicking(true)}
        className="press flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-default py-2.5 text-xs font-medium text-muted transition hover:border-accent hover:text-accent"
      >
        <Plus size={14} />
        Dodaj još jednu marku
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------- */
/* The same two steps, single-select, for the listing form.                                     */
/* ------------------------------------------------------------------------------------------- */

interface PickerProps {
  makeId?: number;
  modelId?: number;
  onChange: (next: { makeId?: number; modelId?: number }) => void;
}

export function MakeModelPicker({ makeId, modelId, onChange }: PickerProps) {
  const { data: reference } = useReference();
  const modelsByMake = useModelsByMake();
  const { reveal } = useBrandReveal();

  const make = reference?.makes.find((item) => item.id === makeId);
  const models = makeId ? (modelsByMake.get(makeId) ?? []) : [];

  if (!make) {
    return (
      <MakeGrid
        selectedIds={[]}
        onPick={(picked) => {
          reveal(picked);
          // The model always belongs to the make, so changing the make can never keep the model.
          onChange({ makeId: picked.id, modelId: undefined });
        }}
        emptyHint="Izaberite marku vozila."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5 rounded-xl border border-accent bg-accent-soft p-2.5">
        <BrandMark make={make} size="md" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-accent">
          {make.name}
        </span>
        <button
          type="button"
          onClick={() => onChange({ makeId: undefined, modelId: undefined })}
          className="press rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted transition hover:text-danger"
        >
          Promeni
        </button>
      </div>

      <ModelChips
        models={models}
        selectedIds={modelId ? [modelId] : []}
        onToggle={(model) => onChange({ makeId, modelId: model.id === modelId ? undefined : model.id })}
      />
    </div>
  );
}
